<script>
/**
 * 감정평가 목록 페이지.
 *
 * 기능:
 * - 감정예약 목록 조회 (서버 페이지네이션)
 * - 검색 필터: 키워드(회원명·예약번호) / 상태 / 예약일 기간(기본 1개월)
 * - 행 클릭 → /appraisal/:code 상세 페이지 이동
 * - 공통 AppPagination 컴포넌트 재사용
 *
 * 권한:
 * - HQ 관리자: 전체 예약 목록 조회
 * - 지점 관리자: 자기 지점 예약 목록만 조회 (서버에서 자동 필터링)
 */
import { getAppraisalList } from '@/api/appraisal.js'
import AppPagination from '@/components/common/AppPagination.vue'

export default {
  name: 'AppraisalListPage',

  components: { AppPagination },

  data() {
    /**
     * 기본 검색 기간 계산.
     * 예약은 미래 날짜로도 잡힐 수 있으므로 종료일을 현재 +1개월로 설정한다.
     * 시작일은 현재 -15일로 설정하여 최근 처리된 예약도 포함한다.
     */
    const toDate = (d) => d.toISOString().substring(0, 10)
    const today = new Date()
    const defaultStart = new Date(today)
    defaultStart.setDate(defaultStart.getDate() - 15)  // 오늘 -15일
    const defaultEnd = new Date(today)
    defaultEnd.setMonth(defaultEnd.getMonth() + 1)     // 오늘 +1개월

    return {
      /* ── 검색 조건 ── */
      search: {
        keyword: '',
        status: '',
        /** 예약일 시작 (기본: 오늘 -15일) */
        startDate: toDate(defaultStart),
        /** 예약일 종료 (기본: 오늘 +1개월) */
        endDate: toDate(defaultEnd),
      },

      /** 상태 select 옵션 (서버/DB와 동일: WAITING) */
      statusOptions: [
        { title: '전체', value: '' },
        { title: '예약대기', value: 'WAITING' },
        { title: '진행중', value: 'ING' },
        { title: '감정완료', value: 'COMPLETE' },
        { title: '취소', value: 'CANCEL' },
      ],

      /**
       * 상태 chip 색상 맵 (서버에서 내려주는 statusLabel 기준, 사용자 앱과 동일).
       */
      statusLabelColorMap: {
        예약대기: 'info',
        진행중: 'warning',
        감정완료: 'success',
        교환완료: 'success',
        취소: 'error',
      },

      /* ── 목록 데이터 ── */
      items: [],

      /* ── 페이지네이션 ── */
      page: 1,
      size: 10,
      total: 0,

      /* ── UI 상태 ── */
      loading: false,
    }
  },
  watch: {
    '$route.query': {
      handler() {
        this.applySearchFromRoute()
        this.fetchList()
      },
      deep: true,
    },
  },

  mounted() {
    this.applySearchFromRoute()
    this.fetchList()
  },

  methods: {
    applySearchFromRoute() {
      const q = this.$route.query || {}
      if (q.keyword !== undefined && q.keyword !== null) this.search.keyword = String(q.keyword)
      if (q.status !== undefined && q.status !== null) this.search.status = String(q.status)
      if (q.startDate !== undefined && q.startDate !== null) this.search.startDate = String(q.startDate)
      if (q.endDate !== undefined && q.endDate !== null) this.search.endDate = String(q.endDate)
      const p = parseInt(String(q.page || ''), 10)
      if (!Number.isNaN(p) && p > 0) this.page = p
      const s = parseInt(String(q.size || ''), 10)
      if (!Number.isNaN(s) && s > 0) this.size = s
    },
    /**
     * 서버에서 감정예약 목록을 조회한다.
     * 검색 조건 + 페이지네이션 파라미터를 전달하고 결과를 items / total에 세팅한다.
     */
    async fetchList() {
      this.loading = true
      try {
        const res = await getAppraisalList({
          keyword:   this.search.keyword   || undefined,
          status:    this.search.status    || undefined,
          startDate: this.search.startDate || undefined,
          endDate:   this.search.endDate   || undefined,
          page:      this.page,
          size:      this.size,
        })
        if (res.data?.code === 0) {
          this.items = res.data.data?.items || []
          this.total = res.data.data?.total || 0
        }
      } catch (e) {
        console.error('[AppraisalListPage] fetchList error:', e)
      } finally {
        this.loading = false
      }
    },

    /** 검색 버튼 클릭: 페이지를 1로 리셋 후 조회 */
    handleSearch() {
      this.page = 1
      this.fetchList()
    },

    /** 초기화 버튼 클릭: 검색 조건 + 페이지 초기화 후 재조회 */
    handleReset() {
      const toDate = (d) => d.toISOString().substring(0, 10)
      const today = new Date()
      const defaultStart = new Date(today)
      defaultStart.setDate(defaultStart.getDate() - 15)  // 오늘 -15일
      const defaultEnd = new Date(today)
      defaultEnd.setMonth(defaultEnd.getMonth() + 1)     // 오늘 +1개월
      this.search = {
        keyword: '',
        status: '',
        startDate: toDate(defaultStart),
        endDate: toDate(defaultEnd),
      }
      this.page = 1
      this.fetchList()
    },

    /** 페이지 변경 (AppPagination emit) */
    onPageChange(newPage) {
      this.page = newPage
      this.fetchList()
    },

    /** 페이지당 항목 수 변경 (AppPagination emit) */
    onSizeChange(newSize) {
      this.size = newSize
      this.page = 1
      this.fetchList()
    },

    /** 행 클릭 → 감정 상세 페이지 이동 */
    goToDetail(item) {
      this.$router.push(`/appraisal/${item.appraisalBookingCode}`)
    },

    /** 상태 chip 색상 (서버 statusLabel 기준) */
    getStatusColor(statusLabel) {
      return this.statusLabelColorMap[statusLabel] || 'default'
    },

    /**
     * 날짜/시간 문자열을 'yyyy-MM-dd HH:mm' 형식으로 포맷한다.
     * null이면 '-' 반환.
     */
    formatDateTime(val) {
      if (!val) return '-'
      return String(val).substring(0, 16).replace('T', ' ')
    },

    /**
     * 날짜/시간 문자열을 'yyyy-MM-dd' 형식으로 포맷한다.
     * null이면 '-' 반환.
     */
    formatDate(val) {
      if (!val) return '-'
      return String(val).substring(0, 10)
    },
  },
}
</script>

<template>
  <div>
    <!-- 페이지 헤더 -->
    <div class="d-flex align-center justify-space-between mb-1">
      <div>
        <div class="text-h5 font-weight-bold">감정평가 관리</div>
        <div class="text-body-2 text-grey mt-1">감정예약 접수 목록 조회 및 감정 처리</div>
      </div>
    </div>

    <!-- 검색 영역 -->
    <v-card rounded="lg" elevation="1" class="mt-5 mb-5">
      <v-card-item class="py-3 bg-orange-lighten-5">
        <v-card-title class="text-subtitle-2 font-weight-bold">검색 조건</v-card-title>
      </v-card-item>
      <v-card-text class="pt-4">
        <v-row>
          <!-- 키워드 검색 -->
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="search.keyword"
              label="회원 검색"
              placeholder="회원명, 예약번호"
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              @keyup.enter="handleSearch"
            />
          </v-col>
          <!-- 상태 -->
          <v-col cols="12" sm="2">
            <v-select
              v-model="search.status"
              :items="statusOptions"
              item-title="title"
              item-value="value"
              label="상태"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
          <!-- 예약일 시작 -->
          <v-col cols="12" sm="2">
            <v-text-field
              v-model="search.startDate"
              label="예약일 시작"
              type="date"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
          <!-- 예약일 종료 -->
          <v-col cols="12" sm="2">
            <v-text-field
              v-model="search.endDate"
              label="예약일 종료"
              type="date"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
        </v-row>

        <div class="d-flex justify-end ga-2 mt-4 border-t-thin pt-4">
          <v-btn color="primary" prepend-icon="mdi-magnify" @click="handleSearch">검색</v-btn>
          <v-btn variant="outlined" prepend-icon="mdi-refresh" @click="handleReset">초기화</v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- 목록 카드 -->
    <v-card rounded="lg" elevation="1">
      <v-card-item class="py-3">
        <v-card-title class="text-subtitle-1 font-weight-bold">
          감정예약 목록
          <v-chip size="small" color="primary" label class="ml-2">{{ total }}건</v-chip>
        </v-card-title>
      </v-card-item>

      <!-- 로딩 인디케이터 -->
      <v-progress-linear v-if="loading" indeterminate color="primary" />

      <v-table density="compact" class="border-t">
        <thead>
          <tr>
            <th class="text-center">번호</th>
            <th class="text-center">예약번호</th>
            <th class="text-center">회원명</th>
            <th class="text-center">연락처</th>
            <th class="text-center">지점명</th>
            <th class="text-center">예약일</th>
            <th class="text-center">상태</th>
            <th class="text-center">검품</th>
            <th class="text-center">등록일</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in items"
            :key="item.appraisalBookingCode"
            class="appraisal-row"
            style="cursor: pointer;"
            @click="goToDetail(item)"
          >
            <!-- 번호: 전체 건수 기준 역순 -->
            <td class="text-center text-caption text-grey">
              {{ total - (page - 1) * size - index }}
            </td>
            <!-- 예약번호 -->
            <td class="text-center text-caption text-grey-darken-1 font-weight-medium">
              {{ item.appraisalBookingNo || item.appraisalBookingCode }}
            </td>
            <!-- 회원명 -->
            <td class="text-center font-weight-medium">{{ item.userName || '-' }}</td>
            <!-- 연락처 -->
            <td class="text-center text-body-2">{{ item.userPhone || '-' }}</td>
            <!-- 지점명 -->
            <td class="text-center text-body-2">{{ item.branchName || '-' }}</td>
            <!-- 예약일 -->
            <td class="text-center text-caption">{{ formatDate(item.bookingDate) }}</td>
            <!-- 상태 chip (서버에서 statusLabel 내려줌: 예약대기/진행중/감정완료/교환완료/취소) -->
            <td class="text-center">
              <v-chip
                :color="getStatusColor(item.statusLabel)"
                size="x-small"
                label
                variant="tonal"
              >
                {{ item.statusLabel || item.status || '-' }}
              </v-chip>
            </td>
            <td class="text-center">
              <v-chip
                v-if="(item.hqYn || '').toUpperCase() === 'Y'"
                size="x-small"
                color="success"
                label
                variant="tonal"
              >
                완료
              </v-chip>
              <span v-else class="text-caption text-medium-emphasis">미완료</span>
            </td>
            <!-- 등록일 -->
            <td class="text-center text-caption text-grey">{{ formatDateTime(item.insertDt) }}</td>
          </tr>

          <!-- 데이터 없음 -->
          <tr v-if="!loading && items.length === 0">
            <td colspan="9" class="text-center text-grey py-10">
              <v-icon size="40" class="d-block mx-auto mb-2" color="grey-lighten-2">
                mdi-clipboard-search-outline
              </v-icon>
              검색 결과가 없습니다.
            </td>
          </tr>
        </tbody>
      </v-table>

      <!-- 페이지네이션 -->
      <div class="px-4 pb-4">
        <AppPagination
          :total="total"
          :page="page"
          :size="size"
          @update:page="onPageChange"
          @update:size="onSizeChange"
        />
      </div>
    </v-card>
  </div>
</template>

<style scoped>
.appraisal-row:hover {
  background-color: rgba(205, 178, 122, 0.06);
}
</style>
